// Onglet « Prix et marges ».
//
// Ces valeurs pilotent le calcul : elles s'appliquent aux pièces qui ne
// portent pas la leur. Les modifier ne rejoue AUCUN devis passé — leur
// `pricing_snapshot` porte les valeurs de leur époque.

import { useCallback, useState, useEffect } from 'react';
import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import NumberField from '../../ui/NumberField.jsx';
import { Select } from '../../ui/TextField.jsx';
import Button from '../../ui/Button.jsx';
import { ErrorState, SkeletonRows } from '../../ui/States.jsx';
import { useToast } from '../../ui/Toast.jsx';
import { useResource } from '../../lib/useResource.js';
import { api } from '../../lib/api.js';
import { ROUNDING_RULES, ROUNDING_LABELS } from '@shared/pricing.js';


export function PrixPanel({ isAdmin }) {
  const toast = useToast();
  const load = useCallback(() => api.get('/parametres'), []);
  const { data, error, loading, reload } = useResource(load);
  const [pricing, setPricing] = useState(null);
  const [rates, setRates] = useState(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (data) {
      setPricing(data.settings.pricing ?? null);
      setRates(data.settings.rates ?? null);
    }
  }, [data]);

  if (loading || !pricing || !rates) return <SkeletonRows rows={5} />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  async function save(key, value) {
    setPending(true);
    try {
      await api.put(`/parametres/${key}`, value);
      toast.success('Paramètres enregistrés.');
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      <Card>
        <CardHead title="Marges et arrondi par défaut">
          {isAdmin ? (
            <Button size="sm" variant="primary" disabled={pending} onClick={() => save('pricing', pricing)}>
              Enregistrer
            </Button>
          ) : null}
        </CardHead>
        <CardBody className="stack">
          <p className="field__hint">
            Ces valeurs s’appliquent aux pièces qui ne portent pas la leur. Les modifier ne change
            aucun devis déjà enregistré : chacun garde le calcul de son époque.
          </p>
          <div className="grid-2">
            {/* IL N'Y A PLUS DE MARGE. Le taux horaire est un TAUX DE
                VENTE : elle est dedans. L'appliquer par-dessus la
                comptait deux fois, et personne ne savait plus si 70 €/h
                était un coût ou un prix.

                Ce qui reste, ce sont trois coefficients de REVENTE — ce
                que Mertens gagne sur ce qu'il ACHÈTE et refacture. Ils
                ne touchent pas l'usinage. */}
            <NumberField label="Coefficient matière" value={pricing.coefficient_matiere}
              min={0} disabled={!isAdmin}
              hint="Ce que Mertens gagne sur la matière qu'il achète et refacture."
              onChange={(v) => setPricing({ ...pricing, coefficient_matiere: v })} />
            <NumberField label="Coefficient sous-traitance" value={pricing.coefficient_sous_traitance}
              min={0} disabled={!isAdmin}
              hint="S'applique aux traitements sous-traités et aux composants du commerce."
              onChange={(v) => setPricing({ ...pricing, coefficient_sous_traitance: v })} />
            <NumberField label="Coefficient transport" value={pricing.coefficient_transport}
              min={0} disabled={!isAdmin}
              hint="1,00 : le transport est rebillé au coût, sans marge."
              onChange={(v) => setPricing({ ...pricing, coefficient_transport: v })} />
            <NumberField label="Minimum de facturation" unit="€" value={pricing.default_min_invoice}
              min={0} disabled={!isAdmin}
              onChange={(v) => setPricing({ ...pricing, default_min_invoice: v })} />
            <Select label="Arrondi par défaut" value={pricing.default_rounding} disabled={!isAdmin}
              options={ROUNDING_RULES.map((r) => ({ value: r, label: ROUNDING_LABELS[r] }))}
              onChange={(e) => setPricing({ ...pricing, default_rounding: e.target.value })} />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHead title="Calcul du brut" />
        <CardBody className="stack">
          <p className="field__hint">
            À partir des cotes de la pièce finie : la section est majorée puis arrondie au palier
            supérieur, la profondeur est majorée puis calée sur une épaisseur achetable.
          </p>
          <div className="grid-2">
            <NumberField label="Majoration de section" unit="%" value={pricing.raw_section_margin_pct}
              min={0} disabled={!isAdmin}
              onChange={(v) => setPricing({ ...pricing, raw_section_margin_pct: v })} />
            <NumberField label="Palier commercial" unit="mm" value={pricing.raw_step_mm}
              min={0.1} disabled={!isAdmin}
              onChange={(v) => setPricing({ ...pricing, raw_step_mm: v })} />
            <NumberField label="Majoration de profondeur" unit="%" value={pricing.raw_depth_margin_pct}
              min={0} disabled={!isAdmin}
              onChange={(v) => setPricing({ ...pricing, raw_depth_margin_pct: v })} />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHead title="Taux et TVA">
          {isAdmin ? (
            <Button size="sm" variant="primary" disabled={pending} onClick={() => save('rates', rates)}>
              Enregistrer
            </Button>
          ) : null}
        </CardHead>
        <CardBody className="stack">
          <p className="field__hint">
            Deux taux, et deux seulement. Le <strong>taux machine</strong> s’applique au temps de
            cycle ; le <strong>taux humain</strong> au montage, au réglage et à la programmation.
            Aucune machine ne porte de tarif : une machine mal attribuée ne peut donc plus fausser
            un prix.
          </p>
          {/* Ce texte disait « le taux humain étant le plus élevé, une
              minute de réglage coûte plus cher qu'une minute d'usinage ».
              Avec deux taux égaux c'est FAUX. La conclusion reste vraie,
              mais pour une autre raison — et c'est celle-là qu'il faut
              écrire. Un texte qui explique faussement est de la même
              famille qu'un contrôle qui ment. */}
          <p className="field__hint">
            Ces deux taux sont des <strong>taux de vente</strong> : la marge et les temps morts
            d’atelier sont déjà dedans. Aucun coefficient ne s’ajoute par-dessus.
          </p>
          <p className="field__hint">
            Le <strong>montage se compte par lot</strong> et la <strong>programmation par
            projet</strong> : à l’unité ils pèsent autant que des heures de cycle, à cinquante
            pièces ils ont presque disparu. C’est ce qui fait du nombre de prises le premier
            levier du prix en petite série.
          </p>

          {!rates.rates_confirmed ? (
            <div className="warnings" role="alert">
              <p className="caps">Taux pas encore confirmés</p>
              <p>
                Les valeurs ci-dessous sont celles annoncées lors du cadrage. Tant qu’elles ne sont
                pas confirmées, <strong>chaque pièce chiffrée porte un avertissement</strong> — un
                prix bâti sur un taux provisoire n’est pas un prix, c’est une estimation.
              </p>
            </div>
          ) : null}

          <div className="grid-2">
            <NumberField label="Taux machine" unit="€/h" value={rates.machine_rate}
              min={0} disabled={!isAdmin}
              hint="S'applique au temps de cycle, par pièce."
              onChange={(v) => setRates({ ...rates, machine_rate: v, rates_confirmed: false })} />
            <NumberField label="Taux humain" unit="€/h" value={rates.human_rate}
              min={0} disabled={!isAdmin}
              hint="S'applique au réglage et à la programmation, par lot."
              onChange={(v) => setRates({ ...rates, human_rate: v, rates_confirmed: false })} />
            <NumberField label="TVA par défaut" unit="%" value={rates.default_vat_rate}
              min={0} max={100} disabled={!isAdmin}
              onChange={(v) => setRates({ ...rates, default_vat_rate: v })} />
            <NumberField label="Validité des devis" unit="jours" value={rates.validity_days}
              min={0} disabled={!isAdmin}
              onChange={(v) => setRates({ ...rates, validity_days: v })} />
          </div>

          {isAdmin ? (
            <label className="row">
              <input
                type="checkbox"
                aria-label="Ces deux taux sont confirmés par l’atelier"
                checked={Boolean(rates.rates_confirmed)}
                onChange={(e) => setRates({ ...rates, rates_confirmed: e.target.checked })}
              />
              <span>
                Ces deux taux sont confirmés par l’atelier. Décochez-les si vous les modifiez sans
                les avoir vérifiés.
              </span>
            </label>
          ) : null}
        </CardBody>
      </Card>
    </>
  );
}

export default PrixPanel;
